// Web analytics contract — the ONLY file that may import posthog-js.
// Every event is coarse and PII-free: no email, no name, no free text.
// Other files only call track(); this module owns the transport.
//
// TWO CONSENT TIERS, AND WHAT EACH ONE DOES (2026-09-05).
//
// The site asks once, in a small bar at the bottom of the page
// (src/components/layout/ConsentBar.tsx). Until it is answered — and forever, if
// the answer is Decline — the site runs the DEFAULT tier:
//
//   DEFAULT / DECLINED
//   · `persistence: "sessionStorage"`. No cookie. One entry in this tab's
//     sessionStorage holds the anonymous id, so a hard navigation inside the tab
//     (a CTA that does a full page load) stays one visit instead of reading as a
//     drop-off. It is gone when the tab closes and it is not readable by any
//     other tab, site, or visit.
//   · No session replay, no heatmaps: `disable_session_recording: true`,
//     `capture_heatmaps: false`, and `disable_external_dependency_loading: true`
//     so posthog-js cannot fetch its recorder bundle even if something asked it
//     to. Nothing is loaded from PostHog's host at all.
//   · Everything else this file names — page views, section views, scroll depth,
//     clicks, form steps — still fires, because none of it is a cookie and none
//     of it identifies a person.
//
//   ACCEPTED
//   · `persistence: "localStorage+cookie"`, so a returning visitor is the same
//     anonymous id and a funnel can span two visits.
//   · Session replay ON, with every input value masked and ALL page text masked
//     (`maskAllInputs: true`, `maskTextSelector: "*"`), and the whole waitlist
//     form blocked outright (`ph-no-capture` on its root, which rrweb replaces
//     with a placeholder block).
//   · Heatmaps ON (`capture_heatmaps`).
//   · Replay needs posthog-js's recorder bundle, which is fetched from PostHog's
//     host. That is the ONE third-party script on this site, it is loaded only
//     after Accept, and the privacy page says exactly that.
//
// The choice is stored in localStorage under `capnolog_consent` as
// `{choice, at}` and re-applied on every load. Nothing about it is sent to a
// server except the `consent_choice` event, which carries the word "granted" or
// "denied" and nothing else.
//
// WHAT IS TRUE OF BOTH TIERS:
//   · No IP, no place. Every event carries `$ip: null` and
//     `$geoip_disable: true`, the same instruction the extension sends
//     (apps/extension/src/core/analytics/posthog.ts:122). PostHog therefore
//     stores neither the address nor a country derived from it.
//   · No person. identify() is never called and `person_profiles` is
//     "identified_only", so no person profile is ever created.
//   · Nothing typed. Autocapture is restricted to click/submit on
//     a/button/form, posthog-js refuses to read the value or the text of an
//     input, select, textarea or contenteditable, and `value`/`placeholder` are
//     in the attribute ignorelist on top of that.
//   · No signal ignored. Do Not Track and Global Privacy Control are checked
//     BEFORE init: with either set, posthog-js is never even downloaded, the
//     consent bar is never shown, and no request is made.
//   · Script errors, reduced. An uncaught error or unhandled rejection is sent
//     as `$exception` (watchErrors below), and before_send cuts it to the type,
//     a message with quoted text, emails and long numbers removed, and
//     file/function/line frames — ./exception-redaction.ts.
//
// Adding a member to WebEvent, or a property to an existing event, is a change
// to what we collect and therefore a change to src/pages/privacy.tsx and
// docs/ops/web-funnel.md in the same commit.
import {
  COMPARE_ROUTE_SLUGS,
  LIVE_ADVERTORIAL_ROUTE_SLUGS,
} from "@/client-route-slugs";

import { DEFAULT_POSTHOG_HOST, DEFAULT_POSTHOG_KEY } from "./analytics.config";
import { isReloadingForChunk } from "./chunk-reload";
import { moveHomeArmToTier, storedHomeArm } from "./home-arm";
import { redactException, stripAdClickIds } from "./exception-redaction";
import type { ReferrerClass } from "./referrer-class";

import type { PostHog } from "posthog-js";

export type WebEvent =
  // ── the funnel ────────────────────────────────────────────────────────────
  | "page_view"
  | "landing_view"
  | "scroll_depth"
  | "cta_click"
  | "waitlist_start"
  | "waitlist_step"
  | "waitlist_complete"
  | "waitlist_error"
  | "audience_card_click"
  | "pricing_view"
  | "tier_cta_click"
  // Program card only: `roster_band` is the size band the coordinator picked
  // before pressing the inquiry CTA ("" when none). LUX-30.
  | "program_inquiry_click"
  // The launch sticker beside the Premium price is pressed (it opens the App
  // Store redeem sheet). Cannot fire until BRAND.appStoreUrl is set. D-94.
  // Also the same offer's line on an invite page, /join/* (D-96); the
  // `path_template` super-property tells the two apart, so no new property.
  | "launch_offer_click"
  | "store_badge_click"
  | "privacy_mechanism_view"
  | "outbound_click"
  // ── where exactly they fall off (2026-09-05) ──────────────────────────────
  | "section_view"
  | "section_time"
  | "vignette_view"
  // ── the try-it demo (2026-09-29) ──────────────────────────────────────────
  // `demo_try_complete {taps, kind, on_brief}`: the try-it phone on /demo saved
  // a case. Taps, never seconds: the demo has no clock (owner, 2026-09-29).
  // `story_step` retired 2026-10-06 with the scroll story it measured (E-393);
  // section_view on `workflow` reads the three-screen band that replaced it.
  | "demo_try_complete"
  | "nav_click"
  | "faq_expand"
  | "time_to_first_interaction"
  | "page_exit"
  | "waitlist_field_focus"
  | "waitlist_abandon"
  // ── the public ideas board, /feedback (plan 086) ──────────────────────────
  // `feedback.voted` carries `idea`, the opaque idea id — never the title, and
  // never the email, which the board exchanges for a token and never keeps.
  | "feedback.viewed"
  | "feedback.voted"
  | "feedback.idea_posted"
  | "feedback.comment_posted"
  | "feedback.email_entered"
  // ── the Case Log Accuracy Test, /case-log-accuracy-test (D-179) ───────────
  // Every property is an enumerable id from src/data/accuracy-test.ts, a count,
  // or a boolean. Never the email, the program name, or a single answer.
  // `accuracy_test_start {specialty, role, training_year}`: the first question
  //   is on screen.
  // `accuracy_test_finish {score, seen, specialty, role, program_link}`: the
  //   score is on screen. `program_link` is true when the taker arrived through
  //   a program edition's link.
  // `accuracy_test_email {specialty, role, updates}`: the email was saved and
  //   the answer key opened. `updates` is the updates box.
  // `accuracy_test_waitlist {specialty}`: a general-surgery taker joined the
  //   waitlist along with the email.
  // `accuracy_test_program_link {roster_band, specialty}`: a program director
  //   or coordinator received a resident link.
  | "accuracy_test_start"
  | "accuracy_test_finish"
  | "accuracy_test_email"
  | "accuracy_test_waitlist"
  | "accuracy_test_program_link"
  // ── the blog, /blog/<slug> (E-364, plan 091 Part D) ───────────────────────
  // `post` is the post's slug from src/data/blog.ts, our own string. Never the
  // email, and never anything a reader typed or pasted.
  // `blog_template_email {post}`: POST /api/v1/notes-template answered 204
  //   and the page said the template is on the way.
  // `blog_import_door {post, door}`: the "Paste your existing notes" button
  //   was pressed. `door` is the closed id "paste_notes". Nothing is pasted,
  //   read, or sent; the button only opens a note that the importer is in
  //   the app.
  // `blog_figure_save {post, figure}`: "Save image" under a figure was
  //   pressed. `figure` is the figure's id in the same file.
  | "blog_template_email"
  | "blog_import_door"
  | "blog_figure_save"
  // ── the consent bar itself ────────────────────────────────────────────────
  | "consent_shown"
  | "consent_choice"
  // ── recovery ──────────────────────────────────────────────────────────────
  // A route chunk failed to load, one reload fixed it, and a route rendered.
  // See ./chunk-reload.ts.
  | "chunk_reload_recovered";

export type WebEventProps = Record<
  string,
  string | number | boolean | null | undefined
>;

// Env first, then the compiled-in default. See analytics.config.ts for why a
// `phc_…` project key is publishable and why the site ships with one: a publish
// with no environment set still measures, which is the whole point.
const KEY =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined)?.trim() ||
  DEFAULT_POSTHOG_KEY;

const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined)
    ?.trim()
    .replace(/\/+$/, "") || DEFAULT_POSTHOG_HOST;

// Dev ergonomics: `pnpm dev` logs each event instead of sending it, so a lane
// wiring a new track() call can see it fire without putting developer traffic in
// the production project. VITE_POSTHOG_DEBUG_SEND=1 opts a dev session back into
// real sending.
const DEBUG_SEND = import.meta.env.VITE_POSTHOG_DEBUG_SEND === "1";
const LOG_ONLY = import.meta.env.DEV && !DEBUG_SEND;

/** The queue exists only for the gap between first track() and init resolving. */
const MAX_PENDING = 50;
const pending: { event: WebEvent; props?: WebEventProps }[] = [];
/** One error in a render loop must not flood the project. See watchErrors(). */
const MAX_ERRORS_PER_PAGE = 10;
const pendingErrors: unknown[] = [];
let errorsSeen = 0;

let client: PostHog | null = null;
let started = false;
/** Set the moment the posthog-js import is kicked off. See scheduleLoad(). */
let loadStarted = false;
let pathTemplateSuper: string | null = null;
let acquisitionSuper: AcquisitionProperties | null = null;

// ───────────────────────────────────────────────────────────────────────────
// Consent
// ───────────────────────────────────────────────────────────────────────────

export type ConsentChoice = "granted" | "denied";

/** The localStorage key. Named in the privacy page, so it is exported, not inline. */
export const CONSENT_STORAGE_KEY = "capnolog_consent";

export interface ConsentRecord {
  readonly choice: ConsentChoice;
  /** ISO date the visitor answered. Never sent anywhere. */
  readonly at: string;
}

let consent: ConsentRecord | null = null;
let consentLoaded = false;

const consentListeners = new Set<(record: ConsentRecord | null) => void>();

/**
 * The stored choice, or null when the visitor has not answered yet.
 * Safe on the server (returns null) and safe when storage throws (Safari
 * private mode, a browser configured to block site data).
 */
export function readConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  if (consentLoaded) return consent;
  consentLoaded = true;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    const choice = (parsed as { choice?: unknown }).choice;
    if (choice !== "granted" && choice !== "denied") return null;
    const at = (parsed as { at?: unknown }).at;
    consent = { choice, at: typeof at === "string" ? at : "" };
    return consent;
  } catch {
    return null;
  }
}

/** Subscribe to consent changes. Returns the unsubscribe function. */
export function onConsentChange(
  listener: (record: ConsentRecord | null) => void,
): () => void {
  consentListeners.add(listener);
  return () => consentListeners.delete(listener);
}

/**
 * Record the visitor's answer: persist it, re-configure posthog-js for the tier
 * it selects, and count it. Called by the consent bar and by nothing else.
 */
export function setConsent(choice: ConsentChoice): void {
  const record: ConsentRecord = {
    choice,
    at: new Date().toISOString().slice(0, 10),
  };
  consent = record;
  consentLoaded = true;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // A browser that blocks storage still gets the tier for this page load;
    // it is simply asked again next time. Never break the page over it.
  }
  moveHomeArmToTier(choice === "granted");
  // Answering the bar IS the first interaction: stop waiting for idle and load
  // now, so `consent_choice` is sent promptly and — on Accept — the recorder is
  // started for the session the visitor just agreed to. applyConsent above is
  // still what re-tiers an ALREADY-live client; loadPostHog is what covers the
  // case where the answer arrived before the chunk did, since init() reads the
  // stored choice itself.
  applyConsent(choice);
  track("consent_choice", { choice });
  if (!LOG_ONLY && KEY && !privacySignalsOptOut()) loadPostHog();
  for (const listener of consentListeners) listener(record);
}

/**
 * The replay masking contract, in one place because both the init config and
 * the Accept path have to use exactly the same one.
 *
 * `maskTextSelector: "*"` masks every rendered text node, not only inputs, so a
 * replay is a wireframe of shapes and interactions. Nothing a visitor typed and
 * nothing a page rendered about them can be read back out of it.
 */
const REPLAY_MASKING = {
  maskAllInputs: true,
  maskTextSelector: "*",
  // The default is already `ph-no-capture`; naming it here is what lets the
  // waitlist form's root class be a documented contract rather than a guess.
  blockClass: "ph-no-capture",
  // `maskAllElementAttributes` is deliberately NOT set. It masks `class`,
  // `style` and `src` too, so a replay would play back as unstyled boxes — and
  // the attributes it would hide are all our own markup, never anything a
  // visitor typed. Text and input values are already masked above.
} as const;

/**
 * Push a tier onto a live posthog-js instance. Every option name here is from
 * posthog-js 1.427.2's own type declarations (@posthog/types
 * `posthog-config.d.ts`), which is the version this site pins.
 */
function applyConsent(choice: ConsentChoice): void {
  const posthog = client;
  if (!posthog) return;
  try {
    if (choice === "granted") {
      posthog.set_config({
        persistence: "localStorage+cookie",
        capture_heatmaps: true,
        // Session replay's recorder is a separate bundle served by PostHog.
        // It can only be fetched once this is false, which is why it is false
        // ONLY here, on the accepted tier.
        disable_external_dependency_loading: false,
        session_recording: { ...REPLAY_MASKING },
      });
      // Flips `disable_session_recording` to false itself and starts recording.
      posthog.startSessionRecording();
    } else {
      posthog.stopSessionRecording();
      posthog.set_config({
        persistence: "sessionStorage",
        capture_heatmaps: false,
        disable_external_dependency_loading: true,
      });
    }
  } catch {
    // ignore — analytics must never break a page
  }
}

// ───────────────────────────────────────────────────────────────────────────
// Privacy signals
// ───────────────────────────────────────────────────────────────────────────

/**
 * Do Not Track / Global Privacy Control. A visitor who has set either is not
 * measured at all — we do not initialize, so posthog-js is never downloaded and
 * no request is made, and the consent bar is never shown (there is nothing to
 * consent to). `navigator.doNotTrack === "1"` is the modern spelling; the other
 * two are the Safari/legacy-IE ones, honored because the intent is identical.
 */
export function privacySignalsOptOut(): boolean {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return false;
  }
  const nav = navigator as Navigator & {
    globalPrivacyControl?: boolean;
    msDoNotTrack?: string;
  };
  const win = window as Window & { doNotTrack?: string };
  const dnt = nav.doNotTrack ?? win.doNotTrack ?? nav.msDoNotTrack;
  if (dnt === "1" || dnt === "yes") return true;
  return nav.globalPrivacyControl === true;
}

/**
 * Should the consent bar be shown at all? No when the visitor has answered, no
 * when DNT/GPC already opted them out of everything, and no when there is
 * nothing running to consent to (dev log-only, or an explicitly blank key).
 */
export function shouldAskForConsent(): boolean {
  if (typeof window === "undefined") return false;
  if (LOG_ONLY) return false;
  if (!KEY) return false;
  if (privacySignalsOptOut()) return false;
  return readConsent() === null;
}

// ───────────────────────────────────────────────────────────────────────────
// Route templates
// ───────────────────────────────────────────────────────────────────────────

/**
 * A path collapsed to a bounded label, so `path_template` can never carry a
 * one-off string a visitor put in the URL bar.
 *
 * Deliberately SHAPE-BASED rather than a copy of the route table: src/routes.ts
 * pulls in the help/audience/compare data registries (all the page copy), and
 * keeping that out of the client bundle is the entire reason
 * src/client-route-slugs.ts exists. A shape rule needs no list and therefore
 * cannot drift out of step with one.
 *
 *   /                       → "/"
 *   /pricing, /anesthesia   → "/pricing", "/anesthesia"   (one lowercase slug)
 *   /help                   → "/help"
 *   /help/rotation-schedule → "/help/[slug]"
 *   /blog/<post>            → "/blog/[slug]"
 *   /join/<invite>          → "/join/[slug]"   (slugs are minted per program)
 *   /acgme-case-minimums/<specialty> → "/acgme-case-minimums/[slug]"
 *   /compare/<known>        → "/compare/<known>" (exactly two, both bounded)
 *   anything else           → "/[other]"
 */
const SLUG = /^[a-z0-9][a-z0-9-]{0,48}$/;

export function pathTemplate(pathname: string): string {
  const path =
    (pathname.split("?")[0] ?? "").split("#")[0]?.replace(/\/+$/, "") || "/";
  if (path === "/") return "/";
  const segments = path.slice(1).split("/");
  const head = segments[0] ?? "";
  if (head === "join") return "/join/[slug]";
  if (head === "help") return segments.length === 1 ? "/help" : "/help/[slug]";
  if (head === "blog") return segments.length === 1 ? "/blog" : "/blog/[slug]";
  // The case-minimums library (E-149): fifty specialty pages under one index.
  // Each used to fall to "/[other]" with every unknown URL (E-488).
  if (head === "acgme-case-minimums")
    return segments.length === 1
      ? "/acgme-case-minimums"
      : "/acgme-case-minimums/[slug]";
  // An advertorial (D-229) keeps its slug once live, so the paid test can
  // compare pages; a draft or an unknown slug collapses.
  if (head === "read" && segments.length === 2) {
    const slug = segments[1] ?? "";
    return LIVE_ADVERTORIAL_ROUTE_SLUGS.includes(slug)
      ? `/read/${slug}`
      : "/read/[slug]";
  }
  if (head === "compare") {
    const slug = segments[1] ?? "";
    return (COMPARE_ROUTE_SLUGS as readonly string[]).includes(slug)
      ? `/compare/${slug}`
      : "/compare/[slug]";
  }
  if (segments.length === 1 && SLUG.test(head)) return `/${head}`;
  return "/[other]";
}

/**
 * A label collapsed to a bounded slug, for the delegated listeners that read an
 * id off the DOM (a nav item's text, a section id, a FAQ question). Lowercase,
 * dashes, 48 characters. Every one of these comes from OUR OWN copy — a visitor
 * cannot put text into a nav link — but the cap is what keeps a breakdown chart
 * finite if a lane ever points one at something long.
 */
export function slugify(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "unknown"
  );
}

function baseSuperProperties(): Record<string, unknown> {
  return {
    // Which Capnolog surface produced the event. The phone and the extension
    // send their own; "web" is this site. Every insight in
    // scripts/posthog-web-funnel.mjs filters on it, and a mobile insight that
    // must exclude the website filters "surface is not set".
    surface: "web",
    // PostHog would otherwise store the request's source address and turn it
    // into a country/region/city. Both off, on every event.
    $ip: null,
    $geoip_disable: true,
    // No `build`: the site's build id is published as a static /build.json at
    // deploy time (scripts/write-build-info.mjs) and is not compiled into the
    // client, so there is nothing to read here without adding a fetch. Version
    // the funnel by date instead — see docs/ops/web-funnel.md.
  };
}

/** Strip undefined so an omitted optional never lands as a property. */
function clean(props?: WebEventProps): Record<string, unknown> | undefined {
  if (!props) return undefined;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function flush(): void {
  if (!client) return;
  while (pending.length > 0) {
    const next = pending.shift();
    if (next) client.capture(next.event, clean(next.props));
  }
  while (pendingErrors.length > 0) {
    client.captureException(pendingErrors.shift());
  }
}

/**
 * Fetch posthog-js and start it. Idempotent, never throws.
 *
 * The consent tier is read HERE rather than when the load was scheduled: the
 * chunk is deliberately not fetched until the page has finished loading (see
 * scheduleLoad below), which is easily long enough for a first-time visitor to
 * have answered the bar in the meantime. Reading it any earlier would
 * initialize on a stale answer, and setConsent's applyConsent() cannot correct
 * that while `client` is still null.
 */
function loadPostHog(): void {
  if (loadStarted) return;
  loadStarted = true;

  const granted = readConsent()?.choice === "granted";

  void import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
        api_host: HOST,

        // ── the consent tier ────────────────────────────────────────────────
        // Default/declined: sessionStorage — no cookie, no localStorage entry,
        // one tab, gone on close. Accepted: the ordinary cookie + localStorage
        // pair, so a returning visitor is one anonymous id.
        persistence: granted ? "localStorage+cookie" : "sessionStorage",
        capture_heatmaps: granted,
        disable_session_recording: !granted,
        // Only the accepted tier may fetch posthog-js's recorder bundle. On the
        // default tier NOTHING is loaded from PostHog's host — replay, surveys
        // and site apps are all behind this one switch.
        disable_external_dependency_loading: !granted,
        session_recording: { ...REPLAY_MASKING },

        // ── what we measure on BOTH tiers ───────────────────────────────────
        // Our own route events are richer than $pageview (they carry the
        // bounded template, not the URL), so PostHog's is off and ours is on.
        capture_pageview: false,
        // $pageleave gives PostHog's own session-duration maths a closing
        // bracket; our `page_exit` carries the scroll depth and the seconds.
        capture_pageleave: true,
        // Web vitals — research item A15 wants load time measured. Network
        // timing is a replay feature and stays off: it would record request
        // URLs, which is exactly the free text this contract refuses.
        capture_performance: { web_vitals: true, network_timing: false },
        // Clicks and submits on real controls only. posthog-js additionally
        // refuses to read the value or text of an input, select, textarea or
        // contenteditable, and honours `ph-no-capture` on an element or any of
        // its parents — which the waitlist form's root carries.
        autocapture: {
          dom_event_allowlist: ["click", "submit"],
          element_allowlist: ["a", "button", "form"],
          element_attribute_ignorelist: ["value", "placeholder"],
        },
        // Frustration signals: three fast clicks in one place, and a click that
        // changed nothing. Both are "this looked clickable and was not".
        rageclick: true,
        capture_dead_clicks: true,
        // Mask advertising ids (gclid, fbclid, …) in the recorded URL. posthog-js
        // masks the value and keeps the parameter (`?fbclid=<masked>`), which
        // the 2026-10-13 audit found in 20% of web URLs, so before_send also
        // strips the parameters outright (stripAdClickIds, E-488).
        mask_personal_data_properties: true,
        // Script errors come from watchErrors() below, not posthog-js's own
        // exception extension: that one is fetched from PostHog's host, which
        // the default tier forbids, and left to remote config it would load on
        // the accepted tier and count every error twice.
        capture_exceptions: false,
        // Every event loses ad click ids from its URL properties; a `$exception`
        // leaves as its type, a redacted message and file/function/line frames.
        before_send: (event) => redactException(stripAdClickIds(event)),

        // ── never ───────────────────────────────────────────────────────────
        disable_surveys: true,
        // Belt and braces: we already refuse to init on DNT/GPC above.
        respect_dnt: true,
        // identify() is never called, so no person profile is ever written.
        person_profiles: "identified_only",
      });
      client = posthog;
      // `home_arm`: the home page A/B arm this visitor was assigned, on every
      // event from then on, so a badge press on any page reads against the
      // arm the visitor landed on (./home-arm.ts). Absent when unassigned.
      const homeArm = storedHomeArm();
      posthog.register({
        ...baseSuperProperties(),
        ...(pathTemplateSuper ? { path_template: pathTemplateSuper } : {}),
        ...(acquisitionSuper ?? {}),
        ...(homeArm ? { home_arm: homeArm } : {}),
      });
      if (granted) {
        // init() alone does not start the recorder; this does.
        posthog.startSessionRecording();
      }
      flush();
    })
    .catch(() => {
      // An ad blocker, an offline first load, a chunk that 404s after a
      // redeploy. Analytics is never load-bearing: drop it and move on.
      pending.length = 0;
      pendingErrors.length = 0;
    });
}

function captureError(error: unknown): void {
  try {
    if (error == null || errorsSeen >= MAX_ERRORS_PER_PAGE) return;
    // A failed route chunk is about to be fixed by a reload; see ./chunk-reload.ts.
    if (isReloadingForChunk()) return;
    errorsSeen += 1;
    if (client) client.captureException(error);
    else pendingErrors.push(error);
  } catch {
    // ignore — analytics must never break a page
  }
}

/**
 * Script errors on the page, as `$exception` — on both tiers, because the event
 * carries no identifier and nothing typed (./exception-redaction.ts). Uncaught
 * errors and unhandled rejections only: a failed image or stylesheet load has no
 * `error` object and is skipped. Capped at MAX_ERRORS_PER_PAGE per page load;
 * an error before posthog-js has loaded waits in `pendingErrors`, like `pending`.
 * These feed PostHog error tracking, and the breakage scanner that watches the
 * CTA routes.
 */
function watchErrors(): void {
  window.addEventListener("error", (event) => captureError(event.error));
  window.addEventListener("unhandledrejection", (event) =>
    captureError(event.reason),
  );
}

/**
 * WHY THE CHUNK WAITS (2026-09-06, performance lane).
 *
 * posthog-js is ~273 KB of uncompressed JavaScript, and the host serves this
 * site with no compression at all — so it was 273 KB competing with React, the
 * route chunk and the stylesheet for the first paint of every page, to send
 * events nobody reads in the first two seconds. It now waits for BOTH:
 *
 *   • the `load` event — everything that paints the page is already fetched;
 *   • an idle callback after that, with a 3s timeout so a permanently busy page
 *     still measures.
 *
 * …or for the visitor's first interaction, whichever comes first: a pointer,
 * key, touch or scroll means the session is real and worth recording promptly.
 * An explicit consent answer loads it immediately (setConsent), because that
 * event has to be sent and the chosen tier has to be applied to a live client.
 *
 * NOTHING IS LOST IN THE GAP. track() already queues into `pending` whenever
 * there is no client yet and flush() replays it in order once init resolves, so
 * the page view fired from the first paint still arrives — later, and with the
 * same properties.
 */
const IDLE_TIMEOUT_MS = 3000;
const IDLE_FALLBACK_MS = 1200;
const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "touchstart",
  "scroll",
] as const;

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout: number },
  ) => number;
};

function scheduleLoad(): void {
  let fired = false;

  const stopWaiting = () => {
    for (const type of INTERACTION_EVENTS) {
      window.removeEventListener(type, go, true);
    }
  };

  function go(): void {
    if (fired) return;
    fired = true;
    stopWaiting();
    loadPostHog();
  }

  for (const type of INTERACTION_EVENTS) {
    window.addEventListener(type, go, { capture: true, passive: true });
  }

  const afterLoad = () => {
    const idle = (window as IdleWindow).requestIdleCallback;
    if (typeof idle === "function") idle(go, { timeout: IDLE_TIMEOUT_MS });
    else window.setTimeout(go, IDLE_FALLBACK_MS);
  };

  if (document.readyState === "complete") afterLoad();
  else window.addEventListener("load", afterLoad, { once: true });
}

/**
 * Called once from main.tsx, on the client only. Never throws, never blocks,
 * and — as of the deferral above — never fetches anything during the page load
 * it is called from. posthog-js is a dynamic import, so it lands in its own
 * async chunk and is not fetched at all when there is no key, in dev log-only
 * mode, or when the visitor has set DNT/GPC.
 */
export function initAnalytics(): void {
  if (started) return;
  started = true;
  if (typeof window === "undefined") return;
  if (LOG_ONLY) return;
  if (!KEY) return;
  if (privacySignalsOptOut()) return;

  watchErrors();
  scheduleLoad();
}

/**
 * Register the current route as a super property, so every subsequent event
 * (a CTA click, a waitlist step) says which page it happened on without each
 * caller having to pass it.
 */
export function setPathTemplate(template: string): void {
  pathTemplateSuper = template;
  try {
    client?.register({ path_template: template });
  } catch {
    // ignore — analytics must never break a page
  }
}

/** The current route template, for a caller that has no hook handy. */
export function currentPathTemplate(): string {
  return pathTemplateSuper ?? "";
}

/**
 * THE acquisition allowlist. Exactly three properties describe where a visit
 * came from, and nothing else may be added to the super-property set under that
 * heading without landing here first — an unbounded fourth value would be a
 * privacy question and an unreadable breakdown chart at the same time.
 *
 * - `referrer_host` — host only. A referrer URL can carry a search query, and
 *   that is exactly the free text this contract refuses to send.
 * - `utm_source` — slugified, so a campaign tag someone invented cannot become
 *   an unbounded property value. "none" on an untagged visit.
 * - `referrer_class` — the six-way bucket from `./referrer-class`. Bounded by
 *   construction, and the only property that can answer "did an AI assistant
 *   send anyone" without reading a host list off a chart by eye.
 */
export interface AcquisitionProperties {
  readonly referrer_host: string;
  readonly utm_source: string;
  readonly referrer_class: ReferrerClass;
}

/**
 * Where this visit came from, registered ONCE per page load as super
 * properties. See `AcquisitionProperties` for what each one is and why it is
 * bounded.
 *
 * They are super properties rather than page_view properties so that
 * `waitlist_complete` can be broken down by them. A conversion is where the
 * acquisition question is actually answered, and it is three events after the
 * page view that knew the referrer.
 */
export function registerAcquisition(props: AcquisitionProperties): void {
  acquisitionSuper = props;
  try {
    client?.register({ ...props });
  } catch {
    // ignore — analytics must never break a page
  }
}

/**
 * The acquisition properties registered for this page load, or null before the
 * first page view has registered them. Read by the store-link tagger
 * (hooks/useStoreCampaignLinks.ts), which turns them into Apple's campaign
 * token; it never leaves the browser any other way.
 */
export function currentAcquisition(): AcquisitionProperties | null {
  return acquisitionSuper;
}

/** Fire-and-forget. Never throws. */
export function track(event: WebEvent, props?: WebEventProps): void {
  try {
    if (typeof window === "undefined") return;
    if (LOG_ONLY) {
      console.debug("[analytics]", event, {
        ...(pathTemplateSuper ? { path_template: pathTemplateSuper } : {}),
        ...clean(props),
      });
      return;
    }
    if (!KEY) return;
    if (privacySignalsOptOut()) return;
    if (client) {
      client.capture(event, clean(props));
      return;
    }
    if (pending.length < MAX_PENDING) pending.push({ event, props });
  } catch {
    // ignore — analytics must never break a page
  }
}

/**
 * The A/B hook. Returns the variant of a PostHog feature flag, or undefined
 * when analytics is off (no key, DNT/GPC, dev log-only, flags not loaded yet) —
 * so every caller must treat undefined as "show the control".
 * See docs/ops/web-funnel.md "Adding an A/B test".
 */
export function getVariant(flagKey: string): string | boolean | undefined {
  try {
    return client?.getFeatureFlag(flagKey);
  } catch {
    return undefined;
  }
}
